/**
 * Iter 213 — TeamFullProfileTabs : 4 onglets enrichis pour la fiche membre.
 *
 * Onglets ajoutés :
 *  - Accès & Permissions : type d'accès, rôles métier, modules autorisés, matrice par société
 *  - Activité : derniers appels Aircall, prospects assignés, tâches, dépenses (compteurs 30j)
 *  - Sécurité : dernière connexion, sessions actives, alertes 24h
 *  - Performance par rôle : section adaptée selon `business_roles` (formateur, commercial, comptable, coach)
 *
 * Endpoint : GET /api/admin/team/{user_id}/full-profile
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, Shield, Activity, Lock, TrendingUp, AlertTriangle, Clock, Phone, Users, Receipt, BookOpen, ChevronRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const BUSINESS_ROLE_LABELS = {
  formateur: "Formateur", commercial: "Commercial", comptable: "Comptable",
  coach: "Coach", stagiaire: "Stagiaire", sous_traitant: "Sous-traitant",
};
const BUSINESS_ROLE_COLORS = {
  formateur: "#8b5cf6", commercial: "#f43f5e", comptable: "#10b981",
  coach: "#a855f7", stagiaire: "#06b6d4", sous_traitant: "#f59e0b",
};

const ACCESS_LABELS = {
  superadmin: { label: "Superadmin", color: "#dc2626", desc: "Accès cross-tenant" },
  admin: { label: "Admin", color: "#3b82f6", desc: "Admin Blitzz" },
  tenant_admin: { label: "Admin tenant", color: "#3b82f6", desc: "Admin de l'entreprise" },
  user: { label: "Utilisateur", color: "#22c55e", desc: "Accès standard" },
  developpeur: { label: "Développeur", color: "#a855f7", desc: "Lecture étendue + logs" },
  formateur: { label: "Formateur", color: "#8b5cf6", desc: "LMS + sessions" },
  client: { label: "Client", color: "#64748b", desc: "Espace client" },
};

import TeamPunchPanel from "./TeamPunchPanel";
import { WorkScheduleEditor } from "./WorkScheduleEditor";

export default function TeamFullProfileTabs({ userId }) {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/team/${userId}/full-profile`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId) reload(); /* eslint-disable-next-line */ }, [userId]);

  if (loading) {
    return (
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-xs text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement de la fiche…
      </div>
    );
  }
  if (!data) return null;

  const { user, activity, recent_calls, security_alerts_24h, active_sessions, last_login_at, business_roles, role, role_specific, societe_perms } = data;

  return (
    <div className="space-y-6" data-testid="team-full-profile-tabs">

      {/* ──── ACCÈS ──── */}
      {true && (
        <div className="space-y-3" data-testid="section-access">
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
            <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Type d'accès</h4>
            <div className="flex items-center gap-3">
              <span className="text-xs px-2.5 py-1 rounded-md font-bold" style={{ background: (ACCESS_LABELS[role]?.color || "#64748b") + "22", color: ACCESS_LABELS[role]?.color || "#64748b" }}>
                {ACCESS_LABELS[role]?.label || role}
              </span>
              <span className="text-[11px] text-slate-500">{ACCESS_LABELS[role]?.desc || "—"}</span>
            </div>
          </div>

          <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
            <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Rôles métier ({business_roles?.length || 0})</h4>
            {business_roles?.length === 0 ? (
              <p className="text-xs text-slate-500 italic">Aucun rôle métier configuré.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {business_roles.map((b) => (
                  <span key={b} className="text-xs px-2.5 py-1 rounded-md font-bold" style={{ background: (BUSINESS_ROLE_COLORS[b] || "#64748b") + "22", color: BUSINESS_ROLE_COLORS[b] || "#64748b" }}>
                    {BUSINESS_ROLE_LABELS[b] || b}
                  </span>
                ))}
              </div>
            )}
          </div>

          {societe_perms?.length > 0 && (
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Permissions par société ({societe_perms.length})</h4>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-500">
                  <tr><th className="text-left p-2">Société</th><th className="text-left p-2">Niveau</th></tr>
                </thead>
                <tbody>
                  {societe_perms.map((sp, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="p-2 text-slate-300">{sp.societe_id}</td>
                      <td className="p-2"><span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300 uppercase font-bold">{sp.role || "—"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ──── ACTIVITÉ ──── */}
      {true && (
        <div className="space-y-3" data-testid="section-activity">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <ActivityKpi icon={Phone} label="Appels (30j)" value={activity.calls_30d} />
            <ActivityKpi icon={Users} label="Prospects assignés" value={activity.assigned_prospects} />
            <ActivityKpi icon={Activity} label="Tâches (30j)" value={activity.tasks_30d} />
            <ActivityKpi icon={Receipt} label="Notes de frais (30j)" value={activity.expense_reports_30d} />
          </div>
          {recent_calls?.length > 0 && (
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Derniers appels Aircall</h4>
              <div className="space-y-2">
                {recent_calls.map((c) => (
                  <Link key={c.call_log_id} to={`/admin/aircall/calls/${c.call_log_id}`} className="block bg-[#0b0811] hover:bg-white/5 rounded-lg p-2.5 transition-colors" data-testid={`call-link-${c.call_log_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${c.direction === "inbound" ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"}`}>
                          {c.direction === "inbound" ? "ENTRANT" : "SORTANT"}
                        </span>
                        <span className="text-xs text-slate-300 ml-2 font-mono">{c.phone}</span>
                        {c.duration ? <span className="text-[10px] text-slate-500 ml-2">{c.duration}s</span> : null}
                      </div>
                      <ChevronRight className="w-3 h-3 text-slate-500" />
                    </div>
                    {c.ai_summary && <p className="text-[11px] text-slate-400 mt-1 truncate">{c.ai_summary}</p>}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ──── SÉCURITÉ ──── */}
      {true && (
        <div className="space-y-3" data-testid="section-security">
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
            <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Dernière connexion</h4>
            <p className="text-sm text-white">
              {last_login_at ? new Date(last_login_at).toLocaleString("fr-FR") : <span className="text-slate-500 italic">Jamais</span>}
            </p>
          </div>
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
            <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Sessions actives ({active_sessions?.length || 0})</h4>
            {active_sessions?.length === 0 ? (
              <p className="text-xs text-slate-500 italic">Aucune session active enregistrée.</p>
            ) : (
              <div className="space-y-1">
                {active_sessions.slice(0, 5).map((s, i) => (
                  <div key={i} className="bg-[#0b0811] rounded p-2 text-xs flex items-center justify-between">
                    <span className="text-slate-300 font-mono">{s.ip || "—"}</span>
                    <span className="text-[10px] text-slate-500 truncate ml-2 max-w-xs">{s.ua}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
            <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Alertes sécurité (24h) — {security_alerts_24h?.length || 0}</h4>
            {security_alerts_24h?.length === 0 ? (
              <p className="text-xs text-slate-500 italic inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Aucune alerte récente.</p>
            ) : (
              <div className="space-y-1.5">
                {security_alerts_24h.map((a, i) => (
                  <div key={i} className="bg-rose-500/5 border border-rose-500/20 rounded p-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-rose-300 font-bold">{a.kind}</span>
                      <span className="text-[10px] text-slate-500">{a.created_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    {a.context?.url && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{a.context.url}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ──── PERFORMANCE PAR RÔLE ──── */}
      {true && (
        <div className="space-y-3" data-testid="section-perf">
          {Object.keys(role_specific || {}).length === 0 ? (
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-xs text-slate-500">
              <BookOpen className="w-8 h-8 mx-auto mb-2 text-slate-700" />
              Aucun rôle métier ne génère de KPI personnalisé pour cette fiche.<br />
              Configurez le rôle <code className="bg-white/5 px-1 rounded">commercial</code>, <code className="bg-white/5 px-1 rounded">formateur</code>, <code className="bg-white/5 px-1 rounded">comptable</code> ou <code className="bg-white/5 px-1 rounded">coach</code>.
            </div>
          ) : (
            <>
              {role_specific?.formateur && (
                <RolePerfCard title="🧑‍🏫 Performance Formateur" data-testid="perf-formateur">
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <ActivityKpi label="Sessions animées" value={role_specific.formateur.sessions_total} />
                    <ActivityKpi label="Apprenants total" value={role_specific.formateur.learners_count} />
                    <ActivityKpi label="Sessions récentes" value={role_specific.formateur.sessions_animated?.length || 0} />
                  </div>
                  {role_specific.formateur.sessions_animated?.length > 0 && (
                    <div className="space-y-1">
                      {role_specific.formateur.sessions_animated.slice(0, 5).map((s) => (
                        <div key={s.session_id} className="bg-[#0b0811] rounded p-2 text-xs flex items-center justify-between">
                          <span className="text-slate-300">{s.title}</span>
                          <span className="text-[10px] text-slate-500">{s.start_date?.slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </RolePerfCard>
              )}

              {role_specific?.commercial && (
                <RolePerfCard title="💼 Performance Commercial" data-testid="perf-commercial">
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <ActivityKpi label="🔥 HOT" value={role_specific.commercial.hot_count} />
                    <ActivityKpi label="🌤️ WARM" value={role_specific.commercial.warm_count} />
                    <ActivityKpi label="❄️ COLD" value={role_specific.commercial.cold_count} />
                    <ActivityKpi label="Taux conversion" value={role_specific.commercial.conversion_rate + "%"} />
                  </div>
                  {role_specific.commercial.top_prospects?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 font-bold mb-1.5">Top prospects scorés</div>
                      <div className="space-y-1">
                        {role_specific.commercial.top_prospects.slice(0, 5).map((p) => (
                          <Link key={p.prospect_id} to={`/admin/prospect/${p.prospect_id}`} className="block bg-[#0b0811] hover:bg-white/5 rounded p-2 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-300">{p.name || p.email}</span>
                              <span className="text-amber-300 font-bold">{p.ai_evaluation?.score || "—"}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </RolePerfCard>
              )}

              {role_specific?.comptable && (
                <RolePerfCard title="🧮 Performance Comptable" data-testid="perf-comptable">
                  <div className="grid grid-cols-2 gap-2">
                    <ActivityKpi label="Notes approuvées" value={role_specific.comptable.expense_reports_approved} />
                    <ActivityKpi label="Notes refusées" value={role_specific.comptable.expense_reports_rejected} />
                  </div>
                </RolePerfCard>
              )}

              {role_specific?.coach && (
                <RolePerfCard title="🧗 Performance Coach" data-testid="perf-coach">
                  <ActivityKpi label="Accompagnements actifs" value={role_specific.coach.active_count} />
                  {role_specific.coach.accompaniments?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {role_specific.coach.accompaniments.slice(0, 5).map((a) => (
                        <div key={a.accompagnement_id} className="bg-[#0b0811] rounded p-2 text-xs flex items-center justify-between">
                          <span className="text-slate-300">{a.client_name}</span>
                          <span className="text-[10px] text-slate-500">{a.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </RolePerfCard>
              )}
            </>
          )}
        </div>
      )}

      {/* ──── POINTAGES (Iter 241) ──── */}
      <TeamPunchPanel userId={userId} />
      {/* ──── HORAIRES & RAPPELS (Iter 337) ──── */}
      <WorkScheduleEditor userId={userId} />
    </div>
  );
}

function ActivityKpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#0b0811] rounded-lg border border-white/5 p-2.5">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold inline-flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3" /> : null} {label}
      </div>
      <div className="text-base font-bold text-white mt-1">{value ?? 0}</div>
    </div>
  );
}

function RolePerfCard({ title, children, ...props }) {
  return (
    <div className="bg-[#14101c] border border-white/10 rounded-xl p-4" {...props}>
      <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">{title}</h4>
      {children}
    </div>
  );
}
